import React, { useState, useEffect, useMemo } from "react"
import { observer } from "mobx-react-lite"
import { useSafeAreaInsets } from "~/ui/safe-area"
import styled, { useTheme } from "~/ui/styled"
import { ScrollView, FlatList } from "~/ui/primitives"
import dayjs from "dayjs"
import isoWeek from "dayjs/plugin/isoWeek"
import {
  StartFlex,
  ColumnFlex,
  ColumnCenterFlex,
  CenterFlex,
  Text,
  BetweenFlex,
  IconFont,
  Button,
  Grid,
} from "~components/El"
import { useStores } from "~models"
import { usePopup } from "~hooks/usePopup"
import { openDatePickerInBottomPopup } from "~components/DatePicker"
import { globalToast, globalSpinner, truncateDecimal } from "~utils"
import { flexPageContainer, scrollViewFillStyle } from "~/ui/scroll-layout"
dayjs.extend(isoWeek)

const fmt = "YYYY-MM-DD"
const options = [
  {
    label: "今天",
    value: `${dayjs().startOf("day").format(fmt)}~${dayjs().endOf("day").format(fmt)}`,
    start_date: dayjs().startOf("day").format(fmt),
    end_date: dayjs().endOf("day").format(fmt),
  },
  {
    label: "本周",
    value: `${dayjs().startOf("isoWeek").format(fmt)}~${dayjs().endOf("isoWeek").format(fmt)}`,
    start_date: dayjs().startOf("isoWeek").format(fmt),
    end_date: dayjs().endOf("isoWeek").format(fmt),
  },
  {
    label: "本月",
    value: `${dayjs().startOf("month").format(fmt)}~${dayjs().endOf("month").format(fmt)}`,
    start_date: dayjs().startOf("month").format(fmt),
    end_date: dayjs().endOf("month").format(fmt),
  },
  {
    label: "自定义",
    value: `${dayjs().startOf("day").format(fmt)}~${dayjs().endOf("day").format(fmt)}`,
    start_date: dayjs().startOf("day").format(fmt),
    end_date: dayjs().endOf("day").format(fmt),
  },
]
const Title = ({ icon = "", iconColor = "", title = "" }) => {
  return (
    <ColumnFlex width="100%">
      <StartFlex gap="6px" py={12}>
        <IconFont name={icon} color={iconColor} size={16} />
        <Text color={"text"} fontSize={"14px"} fontWeight="600">
          {title}
        </Text>
      </StartFlex>
      <CenterFlex height="1px" width="100%" bg="#e3e6ec" />
    </ColumnFlex>
  )
}

const Col = ({
  color = "",
  value = "",
  label = "",
  size = "20px",
  gap = "5px",
  labelSize = "14px",
  ...props
}) => {
  return (
    <ColumnCenterFlex width="100%" height="42px" gap={gap} {...props}>
      <Text color={color} fontSize={size} fontWeight="600">
        {value}
      </Text>
      <Text color={"secondaryText"} fontSize={labelSize}>
        {label}
      </Text>
    </ColumnCenterFlex>
  )
}

const customOption = options.find((o) => o.label === "自定义")!

const Filters: React.FC<any> = (props) => {
  const theme = useTheme()
  const popup = usePopup()
  const { current, onChange } = props
  const maxSelectable = useMemo(() => dayjs().endOf("day").toDate(), [])
  const parseRangeDate = (s: string) => dayjs(s, fmt).toDate()

  return (
    <ColumnFlex width="100%" height={'auto'} gap="10px">
      <BetweenFlex width="100%" padding="0 10px" mt={10}>
        <StartFlex gap="6px" width="auto" minWidth={'max-content'} flexShrink={1}>
          <IconFont name="shijian" color={theme.colors.text} size={14} />
          <Text minWidth={'max-content'} color={theme.colors.text} size={12}>
            查询时间
          </Text>
        </StartFlex>
        <StartFlex gap="6px" width="auto" minWidth={0} flexShrink={1}>
          {options.map((item, index) => {
            const active = current.label === item.label
            return (
              <Button
                key={index}
                bg={active ? "buttonBackground" : "background"}
                borderRadius="6px"
                height="28px"
                px={12}
                onClick={() => {
                  onChange(item)
                }}
              >
                <Text color={active ? "#fff" : "secondaryText"}>{item.label}</Text>
              </Button>
            )
          })}
        </StartFlex>
      </BetweenFlex>
      {current.label === "自定义" && (
        <BetweenFlex width="100%" padding="0 10px" gap="10px">
          <Button
            flex={1}
            bg={"background"}
            px={10}
            borderRadius="6px"
            height="30px"
            onClick={() => {
              openDatePickerInBottomPopup(popup, {
                title: "开始时间",
                value: parseRangeDate(current.start_date),
                maximumDate: dayjs(current.end_date).endOf("day").toDate(),
                onConfirm: (d) => {
                  const start = dayjs(d).format(fmt)
                  const endRaw = current.end_date
                  const end = dayjs(endRaw).isBefore(dayjs(d)) ? start : endRaw
                  onChange({
                    ...customOption,
                    start_date: start,
                    end_date: end,
                    value: `${start}~${end}`,
                  })
                },
              })
            }}
          >
            <BetweenFlex width="100%">
              <Text color="secondaryText">{current.start_date}</Text>
              <IconFont name="shijian" color={theme.colors.secondaryText} size={16} />
            </BetweenFlex>
          </Button>
          <Text>至</Text>
          <Button
            flex={1}
            bg={"background"}
            px={10}
            borderRadius="6px"
            height="30px"
            onClick={() => {
              openDatePickerInBottomPopup(popup, {
                title: "结束时间",
                value: parseRangeDate(current.end_date),
                minimumDate: dayjs(current.start_date).startOf("day").toDate(),
                maximumDate: maxSelectable,
                onConfirm: (d) => {
                  const end = dayjs(d).format(fmt)
                  const startRaw = current.start_date
                  const start = dayjs(d).isBefore(dayjs(startRaw)) ? end : startRaw
                  onChange({
                    ...customOption,
                    start_date: start,
                    end_date: end,
                    value: `${start}~${end}`,
                  })
                },
              })
            }}
          >
            <BetweenFlex width="100%">
              <Text color="secondaryText">{current.end_date}</Text>
              <IconFont name="shijian" color={theme.colors.secondaryText} size={16} />
            </BetweenFlex>
          </Button>
        </BetweenFlex>
      )}
      <CenterFlex height="1px" width="100%" bg="#e3e6ec" />
    </ColumnFlex>
  )
}

const TextInput = styled.TextInput`
  flex: 1;
  padding: 0 10px;
`
const SubordinateItem: React.FC<any> = ({ item }) => {
  return (
    <ColumnCenterFlex
      width="100%"
      height="84px"
      bg="background"
      borderRadius="10px"
      padding="10px"
      mb="10px"
    >
      <BetweenFlex width="100%">
        <Text color={"text"} fontSize={"14px"}>
          {item.user_name}
        </Text>
        <Text color={"#07c160"} fontSize={"14px"} fontWeight="600">
          +{item.agent_rebate_amount}佣金
        </Text>
      </BetweenFlex>
      <Grid columns={4} gap={10}>
        <Col size="14px" labelSize="12px" label="流水" value={item.bet_amount} color="#000" />
        <Col size="14px" labelSize="12px" label="中奖" value={item.payout_amount && Number(item.payout_amount) > 0 ? `+${item.payout_amount}` : item.payout_amount} color="#07c160" />
        <Col size="14px" labelSize="12px" label="输赢" value={item.net_amount && Number(item.net_amount) > 0 ? `+${item.net_amount}` : item.net_amount} color="#fa5251" />
        <Col size="14px" labelSize="12px" label="返水" value={`${truncateDecimal(Number(item.member_rebate_amount) + Number(item.net_loss_rebate_amount), 2)}`} color="#4e98f3" />
      </Grid>
    </ColumnCenterFlex>
  )
}

const AdminMemberItem: React.FC<any> = ({ item }) => {
  // const theme = useTheme()
  const ColorMap = useMemo(() => {
    if (item.chat_member?.is_agent) {
      return {
        color: "#c332d0",
        bgColor: "#eea4f5",
        label: "代理",
      }
    } else if (item.chat_member?.is_collect_bot) {
      return {
        color: "#fa5251",
        bgColor: "#fb9494",
        label: "抢包机器人",
      }
    } else if (item.chat_member?.is_compensate_bot) {
      return {
        color: "#4e98f3",
        bgColor: "#90bef6",
        label: "赔付机器人",
      }
    } else {
      return {  
        color: "#363535",
        bgColor: "#c2c2c2",
        label: "成员",
      }
    }
  }, [item.chat_member])
  return (
    <ColumnCenterFlex
      width="100%"
      height="84px"
      bg="background"
      borderRadius="10px"
      padding="10px"
      mb="10px"
    >
      <BetweenFlex width="100%">
        <Text color={"text"} fontSize={"14px"}>
          {item.user_name || item.username }
        </Text>
        <CenterFlex width='auto' bg={ColorMap.bgColor} borderRadius="10px" padding="2px 5px">
          <Text color={ColorMap.color} fontSize={"10px"}>{ColorMap.label}</Text>
        </CenterFlex>
      </BetweenFlex>
      <Grid columns={4} gap={10}>
        <Col size="14px" labelSize="12px" label="流水" value={item.bet_amount} color="#000" />
        <Col
          size="14px"
          labelSize="12px"
          label="中奖"
          value={
            item.payout_amount && Number(item.payout_amount) > 0 ? `+${item.payout_amount}` : item.payout_amount
          }
          color="#07c160"
        />
        <Col
          size="14px"
          labelSize="12px"
          label="输赢"
          value={item.net_amount && Number(item.net_amount) > 0 ? `+${item.net_amount}` : item.net_amount}
          color="#fa5251"
        />
        <Col
          size="14px"
          labelSize="12px"
          label="返水"
          value={item.total_rebate ?? item.rebate_amount ?? item.member_rebate_amount}
          color="#4e98f3"
        />
      </Grid>
    </ColumnCenterFlex>
  )
}

const Container = styled.View`
  /* margin-bottom: ${({ theme }) => theme.space.xl}; */
  background-color: ${({ theme }) => theme.colors.blockBackground};
  ${flexPageContainer}
`
const RevenueStatistics: React.FC<any> = ({ route }) => {
  const insets = useSafeAreaInsets()
  const { chat_id, isCurrentUserRole } = route.params
  // console.log("isCurrentUserRole", isCurrentUserRole);
  const { apiStore } = useStores()
  const [current, setCurrent] = useState(options[0])
  const [data, setData] = useState<any>({})
  const [groupName, setGroupName] = useState("")
  const [list, setList] = useState<any>([])
  // 获取统计数据
  const getStatisticsData = async () => {
    try {
      globalSpinner.show()
      const res = await apiStore.getGroupChatDataStatistics({
        chat_id: chat_id,
        start_date: current?.start_date,
        end_date: current?.end_date,
      })
      if (res.data) {
        setData(res.data)
      }
      globalSpinner.close()
    } catch (error) {
      globalSpinner.close()
      globalToast.show(error?.message || "获取统计数据失败")
    }
  }
  // 获取数据列表
  const getDataList = async () => {
    try {
      const res = await apiStore.getGroupChatDataList({
        chat_id: chat_id,
        page: 1,
        per_page: 30,
        start_date: current?.start_date,
        end_date: current?.end_date,
      })
      if (res?.data) {
        setList(res.data || [])
      }
    } catch (error) {
    }
  }
  useEffect(() => {
    getStatisticsData()
    if (isCurrentUserRole === "ADMIN") {
      getDataList()
    }
  }, [current])

  const agentList = (data?.chat_agent_stats?.user_list || []) as any[]
  const filteredAgentList = agentList.filter((u) => {
    if (!groupName) return true
    const kw = groupName.trim()
    const name = String(u?.user_name || "")
    return name.includes(kw)
  })

  const adminList = (list || []) as any[]
  const filteredAdminList = adminList.filter((u) => {
    if (!groupName) return true
    const kw = groupName.trim()
    const name = String(u?.user_name || u?.username || "")
    const id = String(u?.user_id || u?.id || "")
    return name.includes(kw) || id.includes(kw)
  })

  const StatsContent = (
    <ColumnFlex width="100%" padding="10px">
      <Title title="基础统计" icon="jctj" iconColor="#07c160" />
      <Grid columns={2} padding={"10px 0"} gap={10}>
        <Col label="总流水" value={data?.user_basic_stats?.total_bet_amount} color="#000" />
        <Col label="总中奖" value={data?.user_basic_stats?.total_payout_amount} color="#030403" />
        <Col label="总输赢" value={data?.user_basic_stats?.total_win_loss} color="#fa5251" />
        <Col label="总返水" value={data?.user_basic_stats?.total_rebate} color="#4e98f3" />
      </Grid>
      <Title title="红包统计" icon="Frame-1" iconColor="#fa5251" />
      <Grid columns={2} padding={"10px 0"} gap={10}>
        <Col label="发出红包" value={data?.user_basic_stats?.total_bets} color="#fa5251" />
        <Col label="领取红包" value={data?.user_basic_stats?.total_payout_count} color="#07c160" />
      </Grid>
      {["COLLECT_BOT"].includes(isCurrentUserRole) && (
        <>
          <Title title="机器人统计" icon="Frame-1" iconColor="#fa5251" />
          <Grid columns={2} padding={"10px 0"} gap={10}>
            <Col
              label="抢到红包数"
              value={String(Number(data?.chat_robot_stats?.dragon_bot_grab_count) + Number(data?.chat_robot_stats?.tiger_bot_grab_count))}
              color="#fa5251"
            />
            <Col
              label="抢到金额"
              value={String(Number(data?.chat_robot_stats?.dragon_bot_grab_amount) + Number(data?.chat_robot_stats?.tiger_bot_grab_amount))}
              color="#07c160"
            />
          </Grid>
        </>
      )}

      {["COMPENSATE_BOT"].includes(isCurrentUserRole) && (
        <>
          <Title title="赔付统计" icon="Frame-1" iconColor="#fa5251" />
          <Grid columns={2} padding={"10px 0"} gap={10}>
            <Col label="赔付次数" value={data?.chat_robot_stats?.compensate_count} color="#fa5251" />
            <Col label="赔付金额" value={data?.chat_robot_stats?.compensate_amount} color="#07c160" />
          </Grid>
        </>
      )}

      {["ADMIN"].includes(isCurrentUserRole) && (
        <>
          <Title title="群组管理" icon="qlgl" iconColor="#4e98f3" />
          <Grid columns={2} padding={"10px 0"} gap={10}>
            <Col label="总成员数" value={data?.chat_revenue_stats?.total_members} color="#4e98f3" />
            <Col label="活跃成员" value={data?.chat_revenue_stats?.active_members} color="#07c160" />
          </Grid>
        </>
      )}

      {["ADMIN"].includes(isCurrentUserRole) && (
        <>
          <Title title="群收益统计" icon="qlgl" iconColor="#4e98f3" />
          <Grid columns={2} padding={"10px 0"} gap={10}>
            <Col label="群总流水" value={data?.chat_revenue_stats?.total_bet_amount} color="#000" />
            <Col label="群总中奖" value={data?.chat_revenue_stats?.total_payout_amount} color="#07c160" />
            <Col label="群总输赢" value={data?.chat_revenue_stats?.total_win_loss} color="#fa5251" />
            <Col label="群总返水" value={data?.chat_revenue_stats?.total_rebate} color="#4e98f3" />
          </Grid>
          <BetweenFlex width="100%" height="50px">
            <Text color={"text"} fontSize={"14px"} fontWeight="600">
              成员明细
            </Text>
            <StartFlex width="50%" height="34px" padding="0 10px" borderRadius="17px" bg="background">
              <IconFont name="ss" color={"secondaryText"} size={16} />
              <TextInput
                placeholder="搜索用户名"
                value={groupName}
                onChangeText={(text) => setGroupName(text)}
              />
            </StartFlex>
          </BetweenFlex>
        </>
      )}

      {["AGENT"].includes(isCurrentUserRole) && (
        <>
          <Title title="下级数据" icon="cjql" iconColor="#4e98f3" />
          <Grid columns={2} padding={"10px 0"} gap={10}>
            <Col label="下级总数" value={data?.chat_agent_stats?.total_members} color="#000" />
            <Col label="今日活跃" value={data?.chat_agent_stats?.active_members} color="#07c160" />
            <Col label="总流水" value={data?.chat_agent_stats?.total_bet_amount} color="#000" />
            <Col label="总中奖" value={data?.chat_agent_stats?.total_payout_amount} color="#07c160" />
            <Col label="总输赢" value={data?.chat_agent_stats?.total_win_loss} color="#fa5251" />
            <Col label="总返水" value={data?.chat_agent_stats?.total_rebate} color="#4e98f3" />
          </Grid>
          <BetweenFlex width="100%" height="50px">
            <Text color={"text"} fontSize={"14px"} fontWeight="600">
              下级详情
            </Text>
            <StartFlex width="50%" height="34px" padding="0 10px" borderRadius="17px" bg="background">
              <IconFont name="ss" color={"secondaryText"} size={16} />
              <TextInput
                placeholder="搜索联系人"
                value={groupName}
                onChangeText={(text) => setGroupName(text)}
              />
            </StartFlex>
          </BetweenFlex>
        </>
      )}
    </ColumnFlex>
  )

  return (
    <>
      <Container>
        <Filters current={current} onChange={setCurrent} />
        {["AGENT"].includes(isCurrentUserRole) ? (
          <FlatList
            data={filteredAgentList}
            keyExtractor={(item, index) => String(index)}
            renderItem={({ item, index }) => <SubordinateItem item={item} index={index} />}
            ListHeaderComponent={StatsContent}
            style={scrollViewFillStyle}
            contentContainerStyle={{ flexGrow: 1, paddingHorizontal: 10, paddingBottom: insets.bottom + 60 }}
          />
        ) : ["ADMIN"].includes(isCurrentUserRole) ? (
          <FlatList
            data={filteredAdminList}
            keyExtractor={(item, index) => String(index)}
            renderItem={({ item, index }) => <AdminMemberItem item={item} index={index} />}
            ListHeaderComponent={StatsContent}
            style={scrollViewFillStyle}
            contentContainerStyle={{ flexGrow: 1, paddingHorizontal: 10, paddingBottom: insets.bottom + 60 }}
          />
        ) : (
          <ScrollView style={scrollViewFillStyle}>
            {StatsContent}
          </ScrollView>
        )}
      </Container>
    </>
  )
}

export default observer(RevenueStatistics)
